import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';

const ProductForm = ({ initialData, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ name: '', price: 0, currency: 'NOK', tax: 25, category: '', unit: 'stk', description: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        tax: Number(initialData.tax ?? initialData.vatRate ?? initialData.vat ?? 25),
        price: initialData.price ?? 0,
        productType: initialData.productType || 'product',
        pricingModel: initialData.pricingModel || 'fixed',
        percentageRate: initialData.percentageRate ?? '',
        fixedPrice: initialData.fixedPrice ?? initialData.price ?? 0,
        canBeBundled: initialData.canBeBundled ?? false,
        status: initialData.status || 'active'
      });
    } else {
      setFormData({ name: '', price: 0, currency: 'NOK', tax: 25, category: '', unit: 'stk', description: '' });
      setFormData(current => ({
        ...current,
        productType: 'product',
        pricingModel: 'fixed',
        percentageRate: '',
        fixedPrice: 0,
        canBeBundled: false,
        status: 'active'
      }));
    }
    setError('');
  }, [initialData]);

  const isPercentagePricing =
    formData.pricingModel === 'commission-percent' ||
    formData.pricingModel === 'value-percent';
  const isCombinationPricing = formData.pricingModel === 'combination';
  const isCustomPricing = formData.pricingModel === 'custom';
  const isAmountPricing = [
    'fixed',
    'hourly',
    'monthly',
    'per-user',
    'per-lead',
    'per-meeting',
    'per-ticket'
  ].includes(formData.pricingModel);

  const handleSubmit = e => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Varenavn må fylles ut.');
      return;
    }

    if (isAmountPricing &&
      (formData.price === '' || !Number.isFinite(Number(formData.price)))) {
      setError('Pris må være et gyldig tall.');
      return;
    }

    if (isCombinationPricing &&
      (formData.fixedPrice === '' || !Number.isFinite(Number(formData.fixedPrice)))) {
      setError('Fast del må være et gyldig tall.');
      return;
    }

    if ((isPercentagePricing || isCombinationPricing) &&
      (formData.percentageRate === '' ||
        !Number.isFinite(Number(formData.percentageRate)) ||
        Number(formData.percentageRate) < 0 ||
        Number(formData.percentageRate) > 100)) {
      setError('Prosent må være et tall mellom 0 og 100.');
      return;
    }

    setError('');
    onSave({
      ...formData,
      name: formData.name.trim(),
      price: Number(formData.price || 0),
      tax: Number(formData.tax ?? 25),
      vatRate: Number(formData.tax ?? 25),
      vat: Number(formData.tax ?? 25),
      fixedPrice: Number(formData.fixedPrice || 0),
      percentageRate: Number(formData.percentageRate || 0),
      canBeBundled: Boolean(formData.canBeBundled),
      status: formData.status || 'active'
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-black text-slate-500 uppercase">Navn på produkt/tjeneste</label>
          <div className="relative mt-1">
            <SafeIcon icon="🏷️" className="absolute left-3 top-3 text-slate-400" />
            <input required value={formData.name} placeholder="F.eks. Møtebooking B2B, Supportpakke, Rekruttering fastpris" onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full pl-10 p-2 border rounded-xl" />
          </div>
        </div>
        <div>
          <label className="text-xs font-black text-slate-500 uppercase">Beskrivelse</label>
          <textarea value={formData.description} placeholder="Beskriv hva som inngår, leveranse, avgrensning og eventuelle vilkår..." onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full p-2 mt-1 border rounded-xl min-h-24 resize-y" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-black text-slate-500 uppercase">Produkttype</label>
            <select value={formData.productType} onChange={(e) => setFormData({ ...formData, productType: e.target.value })} className="w-full mt-1 p-2 border rounded-xl">
              <option value="product">Produkt</option>
              <option value="service">Tjeneste</option>
              <option value="subscription">Abonnement</option>
              <option value="hourly">Timebasert</option>
              <option value="commission">Provisjon</option>
              <option value="performance">Resultatbasert</option>
              <option value="bundle">Pakke</option>
              <option value="other">Annet</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-black text-slate-500 uppercase">Kategori</label>
            <select value={formData.category || 'standard'} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full mt-1 p-2 border rounded-xl">
              <option value="standard">Standard</option>
              <option value="sales">Salg</option>
              <option value="meeting-booking">Møtebooking</option>
              <option value="customer-service">Kundeservice</option>
              <option value="recruitment">Rekruttering</option>
              <option value="support">Support</option>
              <option value="consulting">Konsulenttjeneste</option>
              <option value="saas">Programvare/SaaS</option>
              <option value="course">Kurs</option>
              <option value="other">Annet</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-black text-slate-500 uppercase">Prismodell</label>
            <select value={formData.pricingModel} onChange={(e) => setFormData({ ...formData, pricingModel: e.target.value })} className="w-full mt-1 p-2 border rounded-xl">
              <option value="fixed">Fastpris</option>
              <option value="hourly">Per time</option>
              <option value="monthly">Per måned</option>
              <option value="per-user">Per bruker</option>
              <option value="per-lead">Per lead</option>
              <option value="per-meeting">Per møte</option>
              <option value="per-ticket">Per sak/ticket</option>
              <option value="commission-percent">Provisjon %</option>
              <option value="value-percent">Prosent av verdi</option>
              <option value="combination">Kombinasjon</option>
              <option value="custom">Etter avtale</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-black text-slate-500 uppercase">Enhet</label>
            <select value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} className="w-full mt-1 p-2 border rounded-xl">
              <option value="stk">stk</option>
              <option value="hour">time</option>
              <option value="month">måned</option>
              <option value="user">bruker</option>
              <option value="lead">lead</option>
              <option value="meeting">møte</option>
              <option value="ticket">sak/ticket</option>
              <option value="project">prosjekt</option>
              <option value="candidate">kandidat</option>
              <option value="agreement">avtale</option>
              <option value="other">annet</option>
            </select>
          </div>
        </div>
        {isAmountPricing && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-black text-slate-500 uppercase">Pris / sats</label>
              <div className="relative mt-1">
                <SafeIcon icon="💰" className="absolute left-3 top-3 text-slate-400" />
                <input type="number" value={formData.price} disabled={isPercentagePricing} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="w-full pl-10 p-2 border rounded-xl disabled:bg-slate-100" />
              </div>
              <p className="text-xs text-slate-500 mt-2">Beløp eks. mva for valgt enhet/prismodell.</p>
            </div>
            <div>
              <label className="text-xs font-black text-slate-500 uppercase">MVA %</label>
              <div className="relative mt-1">
                <SafeIcon icon="%" className="absolute left-3 top-3 text-slate-400" />
                <select value={formData.tax} onChange={(e) => setFormData({ ...formData, tax: Number(e.target.value) })} className="w-full pl-10 p-2 border rounded-xl">
                  <option value="25">25%</option>
                  <option value="15">15%</option>
                  <option value="12">12%</option>
                  <option value="0">0%</option>
                </select>
              </div>
            </div>
          </div>
        )}
        {!isAmountPricing && (
          <div>
            <label className="text-xs font-black text-slate-500 uppercase">MVA %</label>
            <div className="relative mt-1">
              <SafeIcon icon="%" className="absolute left-3 top-3 text-slate-400" />
              <select value={formData.tax} onChange={(e) => setFormData({ ...formData, tax: Number(e.target.value) })} className="w-full pl-10 p-2 border rounded-xl">
                <option value="25">25%</option>
                <option value="15">15%</option>
                <option value="12">12%</option>
                <option value="0">0%</option>
              </select>
            </div>
          </div>
        )}
        {isPercentagePricing && (
          <div>
            <label className="text-xs font-black text-slate-500 uppercase">Prosent</label>
            <input type="number" min="0" max="100" value={formData.percentageRate} onChange={(e) => setFormData({ ...formData, percentageRate: e.target.value })} className="w-full mt-1 p-2 border rounded-xl" />
            <p className="text-xs text-slate-500 mt-2">Prosent beregnes av avtalt grunnlag, for eksempel omsetning, lønn, kontraktsverdi eller annet avtalt beløp.</p>
          </div>
        )}
        {isCombinationPricing && (
          <div>
            <label className="text-xs font-black text-slate-500 uppercase">Prosentdel</label>
            <input type="number" min="0" max="100" value={formData.percentageRate} onChange={(e) => setFormData({ ...formData, percentageRate: e.target.value })} className="w-full mt-1 p-2 border rounded-xl" />
            <p className="text-xs text-slate-500 mt-2">Kombinasjon kan brukes for fastpris + provisjon/prosent.</p>
          </div>
        )}
        {isCombinationPricing && (
          <div>
            <label className="text-xs font-black text-slate-500 uppercase">Fast del</label>
            <input type="number" min="0" value={formData.fixedPrice} onChange={(e) => setFormData({ ...formData, fixedPrice: e.target.value })} className="w-full mt-1 p-2 border rounded-xl" />
            <p className="text-xs text-slate-500 mt-2">Kombinasjonsprodukter kan bestå av fastpris + prosent/provisjon. Full pakkebygger kommer senere.</p>
          </div>
        )}
        {isCustomPricing && (
          <p className="text-xs text-slate-500">Pris avtales manuelt i tilbud eller kontrakt.</p>
        )}
        {formData.productType === 'bundle' && (
          <p className="text-xs text-slate-500">Pakkebygging med flere produkter kommer senere. Foreløpig lagres dette som en pakkepris.</p>
        )}
        <div className="flex flex-wrap items-center gap-5">
          <label className="flex items-center gap-2 text-sm font-bold text-slate-600">
            <input type="checkbox" checked={formData.canBeBundled} onChange={(e) => setFormData({ ...formData, canBeBundled: e.target.checked })} />
            Kan inngå i pakke
          </label>
          <p className="text-xs text-slate-500">Gjør produktet tilgjengelig når du senere bygger pakkeløsninger.</p>
          <label className="flex items-center gap-2 text-sm font-bold text-slate-600">
            Status
            <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="p-2 border rounded-xl">
              <option value="active">Aktiv</option>
              <option value="inactive">Inaktiv</option>
            </select>
          </label>
        </div>
        {error && <p className="text-sm font-bold text-red-600" role="alert">{error}</p>}
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-slate-500 font-bold">Avbryt</button>
        <button type="submit" className="px-6 py-2 bg-primary-600 text-white rounded-xl font-bold">Lagre</button>
      </div>
    </form>
  );
};

export default ProductForm